import { useState } from 'react';
import _ from 'lodash';
import { faPauseCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BootstrapTable from 'react-bootstrap-table-next';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { AdminActionCreator } from '../../actions/adminActions';
import { OrderActionCreator } from '../../actions/orderActions';
import { OrderModal } from './OrderModal';
import { getDateStringFromTsecs, getOrgName, getStatusColumns } from './util';
import { orderValuesets } from './util/constants';

/**
 * Displays a table of orders that supports drill down into and individual order
 */
const OrderTable = ({
  orders,
  orderActions,
  limitHeight,
  orgs,
  sort,
  activeTab,
  onOrderModalOpen,
  showHeader = true,
}) => {
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrderUuid, setSelectedOrderUuid] = useState('');

  /**
   * Returns the site size from the inspections on an order
   * Multiple site sizes will be returned as a single string with all inspection power inspected values
   */
  const getInspectionsPower = order => {
    return (
      order &&
      order.inspections &&
      order.inspections
        .map(i => i.power_inspected)
        .reduce((acc, curr) => (acc = acc + curr + ' '), '')
    );
  };

  /**
   * If order on hold, display with a pause sign next to the tracking number
   * @param {} order
   */
  const getTrackingNumber = order => (
    <>
      {order.id}
      &nbsp; &nbsp;
      {order.processing_hold && (
        <FontAwesomeIcon icon={faPauseCircle} style={{ color: 'red' }} />
      )}
    </>
  );

  /**
   * Format the order data for react-bootstrap table
   */
  const formatOrderData = orders => {
    return orders.map(order => {
      return {
        ...order,
        id: getTrackingNumber(order),
        org_name: getOrgName(order),
        farm_name: order && order.solar_farm_name,
        power_inspected: getInspectionsPower(order),
        status: orderValuesets.status[order.status],
        priority: order.priority,
        internal_comments: order.internal_comments,
        data_received_tsecs: getDateStringFromTsecs(order.data_received_tsecs),
        delivered_tsecs: getDateStringFromTsecs(order.delivered_tsecs),
        created_tsecs: getDateStringFromTsecs(order.created_tsecs),
      };
    });
  };

  /**
   * Restricts row priority update between 0 and 10
   */
  const updateRowPriority = (row, increment) => {
    const order = orders.find(o => o.uuid === row.uuid);
    const orderCopy = _.cloneDeep(order);

    if (increment) {
      if (row.priority < 10) {
        orderCopy.priority++;
        orderActions.updatePriority(orderCopy, increment);
      }
    } else {
      if (row.priority > 0) {
        orderCopy.priority--;
        orderActions.updatePriority(orderCopy, increment);
      }
    }
  };

  /**
   * Colors the row based on the PM user color
   * @param {*} row
   */
  const getRowStyle = row => {
    if (row.color) {
      return { background: row.color };
    }
    return {};
  };

  const rowEvents = {
    onClick: (_, row) => {
      // mixpanel.track('Open order edit');

      const tableSelectedOrder = orders.find(order => order.uuid === row.uuid);
      setSelectedOrderUuid(tableSelectedOrder.uuid);

      if (onOrderModalOpen) {
        onOrderModalOpen();
      }
      setShowOrderModal(true);
    },
  };

  return (
    <div className="order-table-container">
      {orders && orders.length > 0 && (
        <BootstrapTable
          keyField="uuid"
          classes={showHeader ? '' : 'hide-order-header'}
          wrapperClasses={
            limitHeight ? 'order-table-wrapper-limited' : 'order-table-wrapper'
          }
          headerClasses={showHeader ? 'multitable-header' : ''}
          bordered
          bootstrap4
          data={formatOrderData(orders)}
          columns={getStatusColumns(updateRowPriority, activeTab)}
          rowEvents={rowEvents}
          rowStyle={getRowStyle}
          sort={sort}
        />
      )}

      <OrderModal
        orderUuid={selectedOrderUuid}
        orderActions={orderActions}
        show={showOrderModal}
        toggle={() => {
          setSelectedOrderUuid('');
          setShowOrderModal(prev => !prev);
        }}
        orgs={orgs}
      />
    </div>
  );
};
const mapDispatchToProps = dispatch => ({
  orderActions: bindActionCreators(OrderActionCreator, dispatch),
  adminActions: bindActionCreators(AdminActionCreator, dispatch),
});
export default connect(null, mapDispatchToProps)(OrderTable);
